import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { Box, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import AttachResourceFormDialog from "../../PORT/Resource/AttachResourceFormDialog";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import ScopeOfWorkFormDialog from "./ScopeOfWorkFormDialog";
import ScopeOfWorkInfoDialog from "./ScopeOfWorkInfoDialog";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";

import { addScopeOfWork, getScopeOfWorkDetails, updateScopeOfWork, deleteScopeOfWork } from "../../../../actions/rmsActions";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        padding: 12,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 18,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

function ScopeOfWorkTable({ items, canUpdate, appraisalId, appCode }) {
    const [openDialog, setOpenDialog] = useState(null);
    const [selected, setSelected] = useState(null);

    const dispatch = useDispatch();
    const handleAdd = (params) => {
        dispatch(addScopeOfWork(appCode, params, appraisalId, ({id}) => dispatch(getScopeOfWorkDetails(id, appCode))));
    };
    const handleEdit = (id, params) => dispatch(updateScopeOfWork(appCode, id, params, appraisalId));
    const handleDelete = (id) => dispatch(deleteScopeOfWork(appCode, id, appraisalId));

    return (
        <Box className="card-content">
            <TableContainer>
                <Table sx={{ minWidth: 500 }} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Scope of Work</StyledTableCell>
                            <StyledTableCell align="left">Area</StyledTableCell>
                            <StyledTableCell align="left">
                                Contract
                            </StyledTableCell>
                            <StyledTableCell>&nbsp;</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items
                            .sort((a, b) => a.id - b.id)
                            .map((row) => (
                                <StyledTableRow key={row?.id}>
                                    <StyledTableCell>
                                        {row?.description}
                                    </StyledTableCell>
                                    <StyledTableCell align="left">
                                        {row?.area}
                                    </StyledTableCell>
                                    <StyledTableCell align="left">
                                        {row?.contract}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Stack direction="row" justifyContent="flex-end" spacing={0} >
                                            {!canUpdate && (
                                                <Tooltip title="View" placement="top" arrow >
                                                    <IconButton
                                                        aria-label="view"
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("view");
                                                        }}
                                                    >
                                                        <PageviewOutlinedIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {!canUpdate &&
                                                row?.resources?.length > 0 && (
                                                    <Tooltip title="Attached resources" placement="top" arrow >
                                                        <IconButton
                                                            aria-label="attachments"
                                                            onClick={() => {
                                                                setSelected(row);
                                                                setOpenDialog("resource");
                                                            }}
                                                        >
                                                            <AttachFileIcon fontSize={"small"} color={"success"} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                            {canUpdate && (
                                                <>
                                                    <Tooltip title="Attached resources" placement="top" arrow >
                                                        <IconButton
                                                            aria-label="attachments"
                                                            onClick={() => {
                                                                setSelected(row);
                                                                setOpenDialog("resource");
                                                            }}
                                                        >
                                                            <AttachFileIcon
                                                                fontSize={"small"}
                                                                color={row?.resources?.length > 0 ? "success" : "default"}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <IconButton
                                                        aria-label="edit"
                                                        disabled={!row.canUpdate}
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("edit");
                                                        }}
                                                    >
                                                        <EditOutlinedIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label="delete"
                                                        disabled={!row.canDelete}
                                                        onClick={() => {
                                                            setSelected(row);
                                                            setOpenDialog("delete");
                                                        }}
                                                    >
                                                        <DeleteOutlinedIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Stack>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        <StyledTableRow key="totals">
                            <StyledTableCell sx={{ backgroundColor: "#FFFFFF" }} colSpan={3} />
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {canUpdate && (
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} >
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        color="secondary"
                        disableElevation={true}
                        aria-label="add scope of work"
                        onClick={() => setOpenDialog("add")}
                    >
                        Add
                    </Button>
                </Stack>
            )}

            {(openDialog === "add" || (openDialog === "edit" && !!selected)) && (
                <ScopeOfWorkFormDialog
                    open
                    formTitle={`${openDialog === "add" ? "Add" : "Edit"} scope of work`}
                    data={openDialog === "edit" && selected}
                    onSave={openDialog === "add" ? handleAdd : (params) => handleEdit(selected.id, params)}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    scroll="paper"
                    submitLabel={`${openDialog === "add" ? "Add" : "Save"}`}
                />
            )}

            {openDialog === "delete" && !!selected && (
                <ConfirmDialog
                    open
                    title="Delete scope of work"
                    content={`The following scope of work will be deleted: ${selected.description}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => handleDelete(selected.id)}
                    onCancel={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                />
            )}

            {openDialog === "resource" && !!selected && (
                <AttachResourceFormDialog
                    open={true}
                    data={selected}
                    onSave={(tempAttached) => handleEdit(selected.id, {...selected, resources: tempAttached})}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                    canUpdate={canUpdate && selected?.canUpdate}
                    appCode={appCode}
                />
            )}

            {openDialog === "view" && !!selected && (
                <ScopeOfWorkInfoDialog
                    open
                    data={selected}
                    onClose={() => {
                        setOpenDialog(null);
                        setSelected(null);
                    }}
                />
            )}
        </Box>
    );
}

export default ScopeOfWorkTable;
